import { useEffect, useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Botao } from '@ui/glass/Botao'

type Props = {
  /** O que o leitor de tela anuncia no gatilho. */
  rotulo: string
  /** O que aparece dentro do botão — normalmente um glifo. */
  gatilho: ReactNode
  children: ReactNode
}

/**
 * Um painel que **flutua** sobre a tela, ancorado no botão que o abriu.
 *
 * ## Ele já foi uma gaveta, e a gaveta estava errada
 *
 * A primeira versão do "⋯" da página de produto abria os links **em linha
 * cheia abaixo do cartão**: o conteúdo empurrava a página para baixo e nada
 * dizia que aquilo pertencia ao "⋯". Menu que empurra layout não lê como menu,
 * lê como uma seção que apareceu.
 *
 * ## Flutuante, e não folha de baixo
 *
 * `FolhaDeBaixo` serve para o que tem muitos itens ou campos — o menu de item
 * dos lembretes, o cadastro de produto. Aqui são três links; cobrir a tela
 * inteira para oferecer três links é peso demais para a ação, e tira de vista
 * justamente o produto sobre o qual se está decidindo.
 *
 * ## Sem `role="menu"`, de propósito
 *
 * O padrão ARIA de menu promete navegação por setas, Home/End e foco em
 * roving tabindex — e coloca o leitor de tela em modo aplicação esperando
 * exatamente isso. Aqui dentro são links comuns, e Tab é como se anda neles.
 * Declarar `role="menu"` sem implementar o teclado do menu seria prometer uma
 * coisa e entregar outra; `aria-expanded` + `aria-controls` dizem a verdade:
 * um botão que mostra e esconde um pedaço da página.
 *
 * ## O fundo que fecha é um `<button>`, e é invisível
 *
 * Sem ele o painel só fecharia pelo próprio gatilho, e tocar em qualquer outro
 * lugar da tela o deixaria aberto — que é o comportamento que faz um menu
 * parecer travado. `aria-hidden` porque não é uma saída anunciável: quem usa
 * teclado sai com Escape.
 */
export function MenuFlutuante({ rotulo, gatilho, children }: Props) {
  const [aberto, setAberto] = useState(false)
  const painel = useRef<HTMLDivElement>(null)
  const id = useId()

  useEffect(() => {
    if (!aberto) return
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') setAberto(false)
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [aberto])

  // O foco entra no painel ao abrir. Sem isso o Tab continuaria na página atrás
  // dele, e quem abriu com o teclado não alcançaria o que pediu.
  useEffect(() => {
    if (aberto) painel.current?.querySelector<HTMLElement>('a, button')?.focus()
  }, [aberto])

  return (
    // `data-esc-proprio` enquanto aberto: o Esc é dele, e o teclado do app não
    // pode também voltar de tela com a mesma tecla (`Atalhos.tsx`).
    <span className="relative inline-flex" data-esc-proprio={aberto ? '' : undefined}>
      {/* A mesma pílula do `Voltar` e do resto: eles dividem a linha do topo,
          e duas bordas diferentes lado a lado leriam como dois sistemas. */}
      <Botao
        type="button"
        aria-expanded={aberto}
        aria-controls={id}
        aria-label={rotulo}
        onClick={() => {
          setAberto((atual) => !atual)
        }}
        className="px-3.5 text-xs"
      >
        {gatilho}
      </Botao>

      {aberto && (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => {
              setAberto(false)
            }}
            className="fixed inset-0 z-30 cursor-default"
          />
          {/* `right-0` e `top-full`: o painel cai do gatilho para baixo e alinha
              pela direita, que é onde o "⋯" costuma ficar. Encostado à esquerda
              ele sairia da tela num gatilho perto da borda direita. */}
          <div
            id={id}
            ref={painel}
            className="absolute top-full right-0 z-40 mt-1 flex min-w-max flex-col rounded-xl border border-[var(--border-glass)] bg-[var(--surface)] p-1 shadow-2xl"
          >
            {children}
          </div>
        </>
      )}
    </span>
  )
}
